// client/src/components/chat/RoomUserList.js
import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { Link } from 'react-router-dom';
import FollowButton from '../social/FollowButton';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

const STATUS_DOT = { online: '#4CAF50', away: '#FF9800', busy: '#f44336' };
const STATUS_LABEL = { online: 'Online', away: 'Away', busy: 'Busy' };

function UserEntry({ userId, info, currentUserId }) {
  const { isFeatureEnabled } = useFeatureFlags();
  const status = info.status || 'online';
  const color = STATUS_DOT[status] ?? STATUS_DOT.online;
  const label = STATUS_LABEL[status] || 'Online';
  const tooltipText = `${info.username} — ${label}${info.awayMessage ? ': ' + info.awayMessage : ''}`;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.5 }}>
      <Tooltip title={tooltipText} placement="left">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0 }}>
          <FiberManualRecordIcon sx={{ fontSize: 8, color, flexShrink: 0 }} />
          <Typography
            variant="caption"
            component={Link}
            to={`/users/${userId}`}
            sx={{
              color: 'text.secondary', textDecoration: 'none', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
              '&:hover': { color: 'primary.main' },
            }}
          >
            {info.username}
          </Typography>
        </Box>
      </Tooltip>
      {isFeatureEnabled('enableFollowSystem') && currentUserId && String(userId) !== String(currentUserId) && (
        <FollowButton targetUserId={userId} currentUserId={currentUserId} size="small" variant="text" />
      )}
    </Box>
  );
}

export default function RoomUserList({ onlineUsers, currentUserId }) {
  const entries = Object.entries(onlineUsers || {});
  const online = entries.filter(([, u]) => u.status !== 'busy' && u.status !== 'away');
  const away = entries.filter(([, u]) => u.status === 'away');
  const busy = entries.filter(([, u]) => u.status === 'busy');

  return (
    <Box sx={{
      width: 160, flexShrink: 0,
      bgcolor: 'rgba(0,0,0,0.2)',
      borderLeft: '1px solid rgba(255,255,255,0.08)',
      overflowY: 'auto',
    }}>
      <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 1 }}>
          IN ROOM ({entries.length})
        </Typography>
      </Box>
      {online.map(([uid, info]) => (
        <UserEntry key={uid} userId={uid} info={info} currentUserId={currentUserId} />
      ))}
      {away.length > 0 && (
        <>
          <Typography variant="caption" sx={{ px: 1.5, color: 'text.disabled', display: 'block', mt: 0.5 }}>AWAY</Typography>
          {away.map(([uid, info]) => <UserEntry key={uid} userId={uid} info={info} currentUserId={currentUserId} />)}
        </>
      )}
      {busy.length > 0 && (
        <>
          <Typography variant="caption" sx={{ px: 1.5, color: 'text.disabled', display: 'block', mt: 0.5 }}>BUSY</Typography>
          {busy.map(([uid, info]) => <UserEntry key={uid} userId={uid} info={info} currentUserId={currentUserId} />)}
        </>
      )}
    </Box>
  );
}
